import React from 'react';
import PropTypes from 'prop-types';
import noop from 'lodash/noop';
import { Flyout } from '@els/els-react--flyout';
import { ButtonWithIcon } from '@els/els-react--button';
import { LinkElement } from '@els/els-react--link-element';
import { Table } from '@els/els-react--table';
import InnerHTML from '@Components/InnerHTML';
import Auth from '@Services/Auth';
import { FEATURES } from '@Constants/rx-ui/constants';
import ResultDetailCard from '@Components/Results/common/ResultDetailCard';

import './DocumentBibliographicInfo.scss';

const safeArray = (input) => [].concat(input);

const defaultTableOptions = {
  highlightHeader: true,
  subtleDividers: true,
  useSemantic: true,
};

const horizontalColumns = [
  { label: 'Attribute', value: 'label' },
  { label: 'Value', value: 'value' },
];

function TableWrapper({ testId, className, a11y, columns, data, noPaddingBottom }) {
  if (!data.length) {
    return null;
  }

  return (
    <div className={`bibliographic-info-table-wrapper${noPaddingBottom ? '' : ' u-els-padding-bottom-2x'}`} data-testid={testId}>
      <Table
        a11y={a11y}
        className={`rx-table rx-table--full-width bibliographic-info-table ${className}`}
        columns={columns}
        data={data}
        rowKey="rowId"
        {...defaultTableOptions}
      />
    </div>
  );
}

function renderLabel(label, testId) {
  return (
    <div className="bibliographic-info-table__label u-els-font-size-base" data-testid={testId}>
      {label}
    </div>
  );
}

function DocumentAssigneesInventorsTableOld({ citation }) {
  const { assignees, inventors } = citation || {};
  const data = [];

  if (assignees) {
    data.push({
      rowId: 'assignees',
      label: renderLabel('Assignees'),
      value: <InnerHTML tag="div" html={assignees} />,
    });
  }

  if (inventors) {
    data.push({
      rowId: 'inventors',
      label: renderLabel('Inventors (Authors)'),
      value: <InnerHTML tag="div" html={safeArray(inventors).join('; ')} />,
    });
  }

  return (
    <TableWrapper
      testId="bibliographic-info-table-assignees"
      className="bibliographic-info-table--assignees"
      a11y={{
        name: 'Bibliographic information assignees and inventors',
        description: 'Patent assignee and inventor information.',
      }}
      columns={horizontalColumns}
      data={data}
    />
  );
}

function DocumentAssigneesInventorsTableNew({ citation = {} }) {
  const { assignees, originalAssignee, inventors, ultimateOwner } = citation || {};
  const assigneeData = [];

  if (assignees) {
    assigneeData.push({
      rowId: 'current-assignee',
      label: renderLabel('Current Patent Assignee'),
      value: <InnerHTML tag="div" html={assignees} />,
    });
  }

  if (ultimateOwner) {
    assigneeData.push({
      rowId: 'ultimate-owner',
      label: (
        <div className="bibliographic-info-table__label bibliographic-info-table__label--with-flyout">
          <span data-testid="bibliographic-info-ultimateOwner" className="u-els-font-size-base">
            Ultimate Owner
          </span>
          <Flyout
            className="bibliographic-info__ultimate-owner-tooltip"
            flyout={
              <div className="bibliographic-info__tooltip-content u-els-font-size-base u-els-color-text">
                <div className="u-els-margin-bottom">
                  The ultimate owner is the harmonized legal entity that ultimately benefits from owning the rights to a patent.
                </div>
                <LinkElement
                  data-e2e="e2e-bibliographic-info-ultimate-owner-link"
                  href="https://service.elsevier.com/app/answers/detail/a_id/37122/supporthub/reaxys/"
                  className="e2e-bibliographic-info-ultimate-owner-link"
                  isExternal>
                  Learn more
                </LinkElement>
              </div>
            }
            shouldCloseOnESC
            showCloseButton
            placement="bottom-start"
            pointerType="primary">
            <ButtonWithIcon
              data-e2e="e2e-bibliographic-info-ultimate-owner"
              sprite="InformationOutlineCircle"
              iconOnly
              type="link"
              linkIconSize="xs"
              className="e2e-bibliographic-info-ultimate-owner"
              onClick={noop}>
              Ultimate Owner
            </ButtonWithIcon>
          </Flyout>
        </div>
      ),
      value: <InnerHTML tag="div" html={ultimateOwner} />,
    });
  }

  if (originalAssignee) {
    assigneeData.push({
      rowId: 'original-assignee',
      label: renderLabel('Original Patent Assignee'),
      value: <InnerHTML tag="div" html={originalAssignee} />,
    });
  }

  if (inventors) {
    assigneeData.push({
      rowId: 'inventors',
      label: renderLabel('Inventors'),
      value: <InnerHTML tag="div" html={safeArray(inventors).join('; ')} />,
    });
  }

  return (
    <TableWrapper
      testId="bibliographic-info-table-assignees"
      className="bibliographic-info-table--assignees"
      a11y={{
        name: 'Bibliographic information assignees and inventors',
        description: 'Bibliographic information for the patent, including assignee, owner, original assignee, and inventor.',
      }}
      columns={horizontalColumns}
      data={assigneeData}
    />
  );
}

function DocumentPatentFamilyTable({ citation }) {
  const { patentFamily } = citation || {};
  const data =
    patentFamily?.map((patent, index) => ({
      rowId: `${patent.patentNo || 'patent'}-${index}`,
      patentNo: <InnerHTML tag="div" html={patent.patentNo} />,
      kindCode: <InnerHTML tag="div" html={patent.kindCode} />,
      publicationDate: <InnerHTML tag="div" html={patent.publicationDate} />,
      fillingDate: <InnerHTML tag="div" html={patent.fillingDate} />,
    })) || [];

  return (
    <TableWrapper
      testId="bibliographic-info-table-patent-family"
      className="bibliographic-info-table--patent-family u-els-font-size-base"
      a11y={{
        name: 'Bibliographic information patent family',
        description: 'Patent family numbers and publication details.',
      }}
      columns={[
        { label: 'Patent Number', value: 'patentNo' },
        { label: 'Kind Code', value: 'kindCode' },
        { label: 'Publication Date', value: 'publicationDate' },
        { label: 'Filing Date', value: 'fillingDate' },
      ]}
      data={data}
    />
  );
}

function DocumentPriorityDataTable({ citation }) {
  const { priorityData } = citation || {};
  const data =
    priorityData?.map((priority, index) => ({
      rowId: `${priority.number || 'priority'}-${index}`,
      number: <InnerHTML tag="div" html={priority.number} />,
      date: <InnerHTML tag="div" html={priority.date} />,
    })) || [];

  return (
    <TableWrapper
      testId="bibliographic-info-table-priority-data"
      className="bibliographic-info-table--priority-data"
      a11y={{
        name: 'Bibliographic information priority data',
        description: 'Priority numbers and dates for the patent.',
      }}
      columns={[
        { label: 'Priority No', value: 'number' },
        { label: 'Priority Date', value: 'date' },
      ]}
      data={data}
    />
  );
}

function DocumentPatentClassificationTable({ citation }) {
  const { mainIpc, secondaryIpc } = citation || {};
  const data = [];

  if (mainIpc) {
    data.push({
      rowId: 'main-ipc',
      label: renderLabel('Main IPC'),
      value: <InnerHTML tag="div" html={safeArray(mainIpc).join('; ')} />,
    });
  }

  if (secondaryIpc) {
    data.push({
      rowId: 'secondary-ipc',
      label: renderLabel('Secondary IPC'),
      value: <InnerHTML tag="div" html={safeArray(secondaryIpc).join('; ')} />,
    });
  }

  return data.length ? (
    <>
      <h4 className="u-els-font-family-bold u-els-margin-bottom-1o2 u-els-font-size-base">Patent Classification</h4>
      <TableWrapper
        testId="bibliographic-info-table-patent-classification"
        className="bibliographic-info-table--patent-classification"
        a11y={{
          name: 'Bibliographic information patent classification',
          description: 'Patent classification codes for the patent.',
        }}
        columns={horizontalColumns}
        data={data}
        noPaddingBottom
      />
    </>
  ) : null;
}

function DocumentBibliographicInfo({ document: citation, closeClick }) {
  const patentSightEnabled = !Auth.isFeatureEnabled(FEATURES.disablePatentSightUIChanges);

  return (
    <ResultDetailCard
      id="bibliographicInfoExpanded-content"
      title={patentSightEnabled ? 'Bibliographic Info' : 'Front page info'}
      className="u-els-margin-top-1o2"
      closeDataE2e="bibliographic-info-button-close"
      onClose={closeClick}>
      <div className="result-detail-block front-page-info rx-reflow-scrollable" data-testid="bibliographic-info-root">
        {patentSightEnabled ? (
          <DocumentAssigneesInventorsTableNew citation={citation} />
        ) : (
          <DocumentAssigneesInventorsTableOld citation={citation} />
        )}
        <DocumentPatentFamilyTable citation={citation} />
        <DocumentPriorityDataTable citation={citation} />
        <DocumentPatentClassificationTable citation={citation} />
      </div>
    </ResultDetailCard>
  );
}

DocumentBibliographicInfo.propTypes = {
  document: PropTypes.object.isRequired,
  closeClick: PropTypes.func.isRequired,
};

TableWrapper.propTypes = {
  testId: PropTypes.string,
  className: PropTypes.string.isRequired,
  a11y: PropTypes.shape({
    name: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
  }).isRequired,
  columns: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  rowHighlight: PropTypes.bool,
  noPaddingBottom: PropTypes.bool,
};
export default DocumentBibliographicInfo;
